import { createSelector } from 'reselect';

import { __ } from '../../i18n/l10n';
import { getIsOutrightFromProps, getMatchIdFromProps } from '../../selectors/_propsGetters_';
import {
  selectEntitiesMatchesEntity,
  selectEntitiesMatchesEntityCategoryId,
  selectEntitiesMatchesEntityCurrentRaceLap,
  selectEntitiesMatchesEntityPeriodId,
  selectEntitiesMatchesEntitySportId,
  selectEntitiesMatchesEntityStoppageTimeAnnounced,
  selectEntitiesMatchesEntityTotalRaceLaps,
} from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesTournaments } from '../../selectors/entities/tournaments/entitiesTournamentsPrimary.selector';
import { selectFavoritesIds } from '../../selectors/favorites/favoritesPrimary.selector';
import { selectMatchCategoriesEntityFlag, selectOutrightMatchFlagId } from '../../selectors/match.selector';
import { selectRuntimeIsStreamHighlightsEnabled } from '../../selectors/runtime/runtimePrimary.selector';
import { selectUserConnected } from '../../selectors/user/userPrimary.selector';
import { MatchEntity, MatchState } from '../../states/entities/matches';
import PeriodIds from '../../types/periodIds';
import { Selector, SelectorMaker } from '../../types/selectors';
import { SportIds } from '../../types/sports';

type MatchCardHeaderPropsType = {
  match?: MatchState;
  flagId?: string;
  isMultimatch: boolean;
  nbEnded?: number;
  nbLive?: number;
  nbPrematch?: number;
  showFavorite: boolean;
  isFavorite: boolean;
  stoppageTimeAnnouncedNbMins?: number;
  streamHighlightsAvailable: boolean;
  shouldHideTimerInfos: boolean;
  categoryId?: number;
  outrightContent?: string;
};

const makeMatchCardHeaderPropsSelector: SelectorMaker<MatchCardHeaderPropsType> = () => {
  const selectShowFavorite: Selector<boolean> = createSelector(selectUserConnected, (isConnected) => isConnected);

  const selectIsFavorite: Selector<boolean> = createSelector(
    [selectFavoritesIds, getMatchIdFromProps],
    (favoriteIds, matchId) => (matchId ? !!~favoriteIds.indexOf(matchId) : false),
  );

  const selectStoppageTimeAnnouncedNbMins: Selector<number | undefined> = createSelector(
    [selectEntitiesMatchesEntityStoppageTimeAnnounced],
    (stoppageTimeAnnounced) => {
      if (typeof stoppageTimeAnnounced === 'string') {
        const [mins] = stoppageTimeAnnounced
          .split(':')
          .reduce((acc, k) => (k ? [...acc, parseInt(k, 10)] : acc), [] as Array<number>);

        if (typeof mins === 'number') {
          return mins;
        }
      }
    },
  );

  const selectShouldHideTimerInfos: Selector<boolean> = createSelector(
    selectEntitiesMatchesEntityPeriodId,
    (matchPeriodId) =>
      !![
        PeriodIds.PERIOD_ID_SOCCER_HALF_TIME,
        PeriodIds.PERIOD_ID_SOCCER_END_REGULAR_TIME,
        PeriodIds.PERIOD_ID_SOCCER_OVERTIME_HALF_TIME,
        PeriodIds.PERIOD_ID_SOCCER_BEFORE_PSO,
        PeriodIds.PERIOD_ID_PSO_START,
      ].find((periodId) => periodId === matchPeriodId),
  );

  const selectFlagId: Selector<string | undefined> = createSelector(
    [
      selectEntitiesMatchesEntity,
      selectEntitiesTournaments,
      getIsOutrightFromProps,
      selectOutrightMatchFlagId,
      selectMatchCategoriesEntityFlag,
      selectEntitiesMatchesEntitySportId,
    ],
    (match, tournaments, isOutright, matchOutrightFlagId, matchCategoryFlagId, matchSportId) => {
      if (matchCategoryFlagId && matchSportId === SportIds.ESPORT) {
        return matchCategoryFlagId;
      }

      const { tournamentId } = match ?? {};

      if (MatchEntity.isMultimatch(match) && tournamentId) {
        return tournaments?.[tournamentId]?.flag;
      }

      if (isOutright) {
        return matchOutrightFlagId;
      }
    },
  );

  const selectOutrightContent: Selector<string | undefined> = createSelector(
    [getIsOutrightFromProps, selectEntitiesMatchesEntityCurrentRaceLap, selectEntitiesMatchesEntityTotalRaceLaps],
    (isOutright, currentLap, totalLaps) => {
      if (isOutright) {
        if (typeof currentLap === 'number' && typeof totalLaps === 'number') {
          return `Lap ${currentLap}/${totalLaps}`;
        }
        return __('Running');
      }
    },
  );

  return createSelector(
    [
      selectEntitiesMatchesEntity,
      selectFlagId,
      selectShowFavorite,
      selectIsFavorite,
      selectStoppageTimeAnnouncedNbMins,
      selectRuntimeIsStreamHighlightsEnabled,
      selectShouldHideTimerInfos,
      selectEntitiesMatchesEntityCategoryId,
      selectOutrightContent,
    ],
    (
      match,
      flagId,
      showFavorite,
      isFavorite,
      stoppageTimeAnnouncedNbMins,
      streamHighlightsEnabled,
      shouldHideTimerInfos,
      categoryId,
      outrightContent,
    ) => ({
      match,
      flagId,
      isMultimatch: MatchEntity.isMultimatch(match),
      nbEnded: match?.nbSubmatchesEnded,
      nbLive: match?.nbSubmatchesLive,
      nbPrematch: match?.nbSubmatchesPending,
      showFavorite,
      isFavorite,
      stoppageTimeAnnouncedNbMins,
      streamHighlightsAvailable: streamHighlightsEnabled && MatchEntity.hasHighlights(match),
      shouldHideTimerInfos,
      categoryId,
      outrightContent,
    }),
  );
};

export default makeMatchCardHeaderPropsSelector;
